import { Body, Heading, Sheet, VStack } from '@hover/blueprint';
import type { SheetContentsProps } from '@hover/blueprint';
import React, { useEffect } from 'react';

import { isBrowser } from 'src/shared/helpers/environment';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import type { HomeownerAppMenuNamesValues } from '../../providers/HomeownerAppProvider';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';

const WELCOME_ACTION_KEY = 'homeowner-welcome-action';

export type WelcomeAction = 'login' | 'signup';

export const storeWelcomeAction = (action: WelcomeAction) => {
  if (!isBrowser()) return;

  window.localStorage.setItem(WELCOME_ACTION_KEY, action);
};

export const clearWelcomeAction = () =>
  window.localStorage.removeItem(WELCOME_ACTION_KEY);

export const getWelcomeMenu = (): HomeownerAppMenuNamesValues | null => {
  if (!isBrowser()) return null;

  switch (window.localStorage.getItem(WELCOME_ACTION_KEY)) {
    case 'login':
      return HomeownerAppMenuNames.WELCOME_BACK;
    case 'signup':
      return HomeownerAppMenuNames.WELCOME_TO;
    default:
      return null;
  }
};

interface WelcomeProps extends SheetContentsProps {
  isSigningUp?: boolean;
}

const WelcomeSheet = ({ isSigningUp, ...props }: WelcomeProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { user, currentMenu } = useHomeownerAppContext();
  const name = user?.firstName || user?.name || '';
  const welcomeText = isSigningUp
    ? t('welcome.welcomeToHover')
    : t('welcome.welcomeBackToHover');
  const heading = name.length ? `${welcomeText}, ${name}` : welcomeText;

  useEffect(() => {
    if (currentMenu !== null || !isBrowser()) return;

    clearWelcomeAction();
  }, [currentMenu]);

  return (
    <Sheet.Contents {...props}>
      <VStack
        align="start"
        data-testid="welcomeSheet"
        mb={isSigningUp ? 100 : 0}
        mt={300}
        pb={200}
      >
        <Heading
          isLight
          maxW="85%"
          mb={isSigningUp ? 200 : 900}
          overflowWrap="break-word"
          size={600}
          textAlign="left"
        >
          {heading}
        </Heading>
        {isSigningUp && (
          <Body color="neutral.600" mb={100} textStyle="body400">
            {t('welcome.favorites')}
          </Body>
        )}
      </VStack>
    </Sheet.Contents>
  );
};

export { WelcomeSheet };
